/**
 * A bounding volume hierarchy, flat enough to hand to a GPU.
 *
 * Built over **anything that has a box and a centre** rather than over
 * triangles, because it is used twice for two different things: once per unique
 * geometry over its triangles, and once over the instances of those geometries.
 * A path tracer that unrolled every instance into world space would have to
 * store the pine tree two thousand two hundred and fifty times — 2,1 million
 * triangles — where storing it once and pointing at it costs 144 kB of
 * matrices. That is the whole reason this is a two-level structure, and the
 * reason the builder is generic.
 *
 * The node layout is the classic 32-byte one, two `vec4`s in WGSL:
 *
 * ```text
 *   0..2  box min
 *   3     internal: index of the left child; leaf: first item
 *   4..6  box max
 *   7     internal: 0; leaf: how many items
 * ```
 *
 * Children are stored adjacently, so the right child is always `left + 1` and
 * only one index has to be kept. A `count` of zero is what says "internal",
 * which is safe because a leaf is never built empty.
 */

/** Eight floats per node; see the layout above. */
export const NODE_STRIDE = 8;

/** Items per leaf, above which a node is split. */
const LEAF_SIZE = 4;

export interface Bvh {
  nodes: Float32Array;
  /** Item indices, reordered so that a leaf's items are contiguous. */
  order: Uint32Array;
  nodeCount: number;
}

export interface Boxed {
  count: number;
  /** Writes the item's box into `out` as min.xyz, max.xyz. */
  bounds(index: number, out: Float32Array): void;
}

/**
 * Build one.
 *
 * Median split on the widest axis of the node's centroid spread — not a full
 * SAH sweep. The difference shows up as a few per cent more box tests on a
 * pathological mesh, and our geometry is the opposite of pathological: terrain
 * is a grid of similar triangles and the props are compact models. Paying for
 * SAH here would be paying for the case we do not have.
 *
 * Falls back to splitting by count when the centroids are degenerate along
 * every axis, which happens on coplanar fans — the terrain is made of them.
 * Without that fallback the recursion never terminates on a flat chunk.
 */
export function buildBvh(items: Boxed): Bvh {
  const count = items.count;
  const order = new Uint32Array(count);
  for (let i = 0; i < count; i++) order[i] = i;

  if (count === 0) {
    // One empty node, so a traversal has something to bounce off rather than
    // reading past the end of the buffer.
    return { nodes: new Float32Array(NODE_STRIDE), order, nodeCount: 1 };
  }

  // Boxes and centroids up front: the split asks for them O(log n) times per
  // item, and recomputing a triangle's box each time is the whole cost of a
  // build.
  const boxes = new Float32Array(count * 6);
  const centroids = new Float32Array(count * 3);
  const scratch = new Float32Array(6);
  for (let i = 0; i < count; i++) {
    items.bounds(i, scratch);
    boxes.set(scratch, i * 6);
    for (let axis = 0; axis < 3; axis++) {
      centroids[i * 3 + axis] = (scratch[axis] + scratch[axis + 3]) / 2;
    }
  }

  // A binary tree over n leaves of at least LEAF_SIZE has fewer than 2n/LEAF
  // internal nodes; 2n is a safe ceiling and the array is trimmed at the end.
  const nodes = new Float32Array(Math.max(1, 2 * count) * NODE_STRIDE);
  let used = 1;

  const enclose = (from: number, to: number, node: number): void => {
    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;

    for (let i = from; i < to; i++) {
      const at = order[i] * 6;
      minX = Math.min(minX, boxes[at]);
      minY = Math.min(minY, boxes[at + 1]);
      minZ = Math.min(minZ, boxes[at + 2]);
      maxX = Math.max(maxX, boxes[at + 3]);
      maxY = Math.max(maxY, boxes[at + 4]);
      maxZ = Math.max(maxZ, boxes[at + 5]);
    }

    const slot = node * NODE_STRIDE;
    nodes[slot] = minX;
    nodes[slot + 1] = minY;
    nodes[slot + 2] = minZ;
    nodes[slot + 4] = maxX;
    nodes[slot + 5] = maxY;
    nodes[slot + 6] = maxZ;
  };

  // Explicit stack rather than recursion: a chunk of terrain is a quarter of a
  // million triangles and the depth is fine, but a build that can blow the JS
  // stack on one unlucky mesh is not worth the two lines it saves.
  const stack: { node: number; from: number; to: number }[] = [
    { node: 0, from: 0, to: count },
  ];

  while (stack.length > 0) {
    const { node, from, to } = stack.pop()!;
    enclose(from, to, node);

    const items = to - from;
    const slot = node * NODE_STRIDE;

    if (items <= LEAF_SIZE) {
      nodes[slot + 3] = from;
      nodes[slot + 7] = items;
      continue;
    }

    // Widest axis of the centroid spread, which is not the same as the widest
    // axis of the box: a long thin triangle can stretch a box without moving
    // any centroid, and splitting along that axis puts everything on one side.
    let axis = 0;
    let widest = -1;
    for (let candidate = 0; candidate < 3; candidate++) {
      let low = Infinity;
      let high = -Infinity;
      for (let i = from; i < to; i++) {
        const value = centroids[order[i] * 3 + candidate];
        low = Math.min(low, value);
        high = Math.max(high, value);
      }
      if (high - low > widest) {
        widest = high - low;
        axis = candidate;
      }
    }

    let middle = from;
    if (widest > 0) {
      // Partition around the midpoint of the spread. Cheaper than sorting and
      // it is what the split needs: an ordering within a side is worthless.
      let low = Infinity;
      let high = -Infinity;
      for (let i = from; i < to; i++) {
        const value = centroids[order[i] * 3 + axis];
        low = Math.min(low, value);
        high = Math.max(high, value);
      }
      const pivot = (low + high) / 2;

      middle = from;
      for (let i = from; i < to; i++) {
        if (centroids[order[i] * 3 + axis] < pivot) {
          const swap = order[i];
          order[i] = order[middle];
          order[middle] = swap;
          middle++;
        }
      }
    }

    // **Neither side may be thinner than a quarter**, and this is a correctness
    // rule rather than a quality one. A spatial split has no lower bound on how
    // lopsided it can be — one triangle far from the rest peels off a leaf per
    // level — and a tree that deep overflows the fixed stack the traversal
    // walks it with. A fixed stack that overflows does not fail: it silently
    // drops whole subtrees, and the picture comes out with holes in places that
    // depend on the geometry rather than on the code.
    //
    // A quarter bounds the depth at log(n)/log(4/3), which is 44 levels for a
    // quarter of a million triangles against the 64 the stack holds. It also
    // covers the degenerate case — coplanar centroids, everything on one side —
    // which the terrain produces constantly, being made of flat fans.
    const least = Math.max(1, items >> 2);
    if (middle - from < least || to - middle < least) middle = (from + to) >> 1;

    const left = used;
    used += 2;
    nodes[slot + 3] = left;
    nodes[slot + 7] = 0;

    stack.push({ node: left, from, to: middle });
    stack.push({ node: left + 1, from: middle, to });
  }

  return { nodes: nodes.subarray(0, used * NODE_STRIDE), order, nodeCount: used };
}

/**
 * Walk one, calling back for every item in every leaf the ray could reach.
 *
 * The reference implementation of what the shader does, and it exists to be
 * **tested against brute force**: the layout above is the only part of this
 * feature whose bugs are silent — a mis-indexed child gives a picture that is
 * merely wrong rather than one that fails. `traverse.wgsl` is a transliteration
 * of this loop, so agreeing with brute force here is what says the layout and
 * the indices are right.
 *
 * `hit` returns the distance along the ray, or a number that is not less than
 * the distance passed in when there is no hit. The nearest hit found so far
 * prunes the rest of the walk, which is the only reason a hierarchy is faster
 * than a list.
 */
export function traverseBvh(
  bvh: Bvh,
  origin: readonly [number, number, number],
  direction: readonly [number, number, number],
  far: number,
  hit: (item: number, best: number) => number
): { t: number; item: number } {
  const inverse: [number, number, number] = [
    1 / direction[0],
    1 / direction[1],
    1 / direction[2],
  ];

  let best = far;
  let found = -1;

  // Sized for a tree over a quarter of a million items; a median split cannot
  // get deeper than log2(n) plus the count-halving fallback.
  const stack = new Int32Array(64);
  let depth = 0;
  stack[depth++] = 0;

  while (depth > 0) {
    const node = stack[--depth];
    const slot = node * NODE_STRIDE;

    if (!hitsBox(bvh.nodes, slot, origin, inverse, best)) continue;

    const count = bvh.nodes[slot + 7];
    if (count > 0) {
      const first = bvh.nodes[slot + 3];
      for (let i = 0; i < count; i++) {
        const item = bvh.order[first + i];
        const t = hit(item, best);
        if (t < best) {
          best = t;
          found = item;
        }
      }
      continue;
    }

    const left = bvh.nodes[slot + 3];
    stack[depth++] = left;
    stack[depth++] = left + 1;
  }

  return { t: best, item: found };
}

/** Slab test. `NaN`s from a zero direction component compare false, which is
 * the answer that keeps a ray parallel to a slab out of boxes it misses. */
function hitsBox(
  nodes: Float32Array,
  slot: number,
  origin: readonly [number, number, number],
  inverse: readonly [number, number, number],
  far: number
): boolean {
  let near = 0;
  let distant = far;

  for (let axis = 0; axis < 3; axis++) {
    const low = (nodes[slot + axis] - origin[axis]) * inverse[axis];
    const high = (nodes[slot + 4 + axis] - origin[axis]) * inverse[axis];
    near = Math.max(near, Math.min(low, high));
    distant = Math.min(distant, Math.max(low, high));
  }

  return distant >= near;
}
